import type { ServiceReportPage, ServiceReportRow } from "@allergy123/contracts/clinical";
import { Button, Card, Field, Select, Status, Table, Td, Th } from "@allergy123/ui";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useClinical } from "../api";
import { errorText } from "../lib/errors";
import { formatDateTime, fullName, kindLabel, statusLabel, zonedToIso } from "../lib/format";
import { useSession } from "../session";

const KINDS = ["test", "retest", "continuation", "office_visit", "adjustment"] as const;
const OUTCOME_LABELS: Record<string, string> = { immunotherapy_ordered: "Immunotherapy ordered", test_only_no_immunotherapy: "Test only, no immunotherapy", office_visit: "Office visit", adjustment: "Adjustment" };

/** Service history (inventory.md 4.14): the practice's reports as immutable documents, downloaded through audited short-lived links (ADR 0007). */
export function ReportsPage() {
  const s = useSession();
  const api = useClinical();
  const timezone = s.location?.timezone ?? "America/Denver";
  const [filters, setFilters] = useState({ locationId: s.location?.locationId ?? "", kind: "", from: "", to: "" });
  const [page, setPage] = useState<ServiceReportPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);

  const request = (cursor?: string) => ({
    locationId: filters.locationId || undefined,
    kind: filters.kind ? [filters.kind as (typeof KINDS)[number]] : undefined,
    from: filters.from ? zonedToIso(filters.from, 0, timezone) : undefined,
    to: filters.to ? zonedToIso(filters.to, 24 * 60, timezone) : undefined,
    limit: 25,
    cursor,
  });

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError(null);
    api.dashboard
      .reports(request())
      .then((res) => live && setPage(res))
      .catch((err: unknown) => live && setError(errorText(err)))
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, filters.locationId, filters.kind, filters.from, filters.to, timezone]);

  async function more() {
    if (!page?.nextCursor) return;
    setLoading(true);
    try {
      const res = await api.dashboard.reports(request(page.nextCursor));
      setPage({ ...res, items: [...page.items, ...res.items] });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setLoading(false);
    }
  }

  async function open(row: ServiceReportRow) {
    if (!row.currentVersion) return;
    setOpening(row.documentId);
    setError(null);
    try {
      const link = await api.documents.access(row.documentId, row.currentVersion.versionId, { disposition: "inline" });
      window.open(link.url, "_blank", "noopener");
    } catch (err) {
      setError(errorText(err));
    } finally {
      setOpening(null);
    }
  }

  return (
    <>
      <div className="mb-8">
        <h1 className="mb-2 text-h1 font-semibold text-anchor">Service reports</h1>
        <p className="max-w-[68ch] text-ink-secondary">Every signed encounter's report, newest first. A report is generated once from the record as it was signed and never regenerated; a correction adds a new version. Each download is recorded.</p>
      </div>
      <Card className="mb-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Select label="Location" options={[{ value: "", label: "All locations" }, ...s.locations.map((l) => ({ value: l.locationId, label: l.name }))]} value={filters.locationId} onChange={(e) => setFilters({ ...filters, locationId: e.target.value })} />
          <Select label="Visit kind" options={[{ value: "", label: "All kinds" }, ...KINDS.map((k) => ({ value: k, label: kindLabel(k) }))]} value={filters.kind} onChange={(e) => setFilters({ ...filters, kind: e.target.value })} />
          <Field label="From" type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} />
          <Field label="To" type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} />
        </div>
      </Card>
      {error && <Status kind="error" label={error} className="mb-4 block" />}
      <Card>
        <Table caption={page ? `${page.total} report${page.total === 1 ? "" : "s"}` : "Loading…"}>
          <thead>
            <tr>
              <Th>Service date</Th>
              <Th>Patient</Th>
              <Th>Visit</Th>
              <Th>Outcome</Th>
              <Th>Report</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {page && page.items.length === 0 && !loading && (
              <tr>
                <Td colSpan={6} className="text-ink-secondary">
                  No reports match.
                </Td>
              </tr>
            )}
            {page?.items.map((r) => (
              <tr key={r.documentId}>
                <Td>
                  <Link to={`/encounters/${r.encounterId}`}>{formatDateTime(r.serviceAt, timezone)}</Link>
                </Td>
                <Td>
                  <Link to={`/patients/${r.patient.patientId}`}>{fullName(r.patient)}</Link>
                  <span className="block text-small text-ink-secondary">Born {r.patient.dateOfBirth}</span>
                </Td>
                <Td>{kindLabel(r.kind)}</Td>
                <Td>{OUTCOME_LABELS[r.outcome] ?? r.outcome}</Td>
                <Td>
                  {r.currentVersion ? (
                    <>
                      {statusLabel(r.currentVersion.status)}
                      <span className="block text-small text-ink-secondary">version {r.currentVersion.revision}</span>
                    </>
                  ) : (
                    statusLabel("queued")
                  )}
                </Td>
                <Td>
                  {r.currentVersion?.status === "ready" ? (
                    <Button variant="secondary" disabled={opening === r.documentId} onClick={() => void open(r)}>
                      Open PDF
                    </Button>
                  ) : (
                    <span className="text-small text-ink-secondary">Not ready</span>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {page?.nextCursor && (
          <div className="mt-4">
            <Button variant="secondary" surface="card" disabled={loading} onClick={() => void more()}>
              Show more
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
